import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import { Command, CommandItem, CommandList } from "@/components/flexkit/Command";
import { DialogDescription, DialogTitle } from "@/components/podkit/dialog/Dialog";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";
import type { NO_PROJECT_SELECTED } from "@/components/projects/ProjectSelect";
import { useContextURL } from "@/hooks/use-context-url";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useEnvironmentClassEntries, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useRequireProject } from "@/hooks/use-require-project";
import { TrackLocations } from "@/hooks/use-segment";
import type { PlainProject } from "@/queries/project-queries";
import { Authentication } from "@/routes/environments/create/components/Authentication";
import { NoEnvModal } from "@/routes/environments/create/components/NoEnvModal";
import {
    activeTabAtom,
    TabsValues,
    type CreateEnvironmentStage,
    type TabValue,
} from "@/routes/environments/create/create-environment-utils";
import { BlankTab } from "@/routes/environments/create/tabs/BlankTab";
import { ProjectTab } from "@/routes/environments/create/tabs/ProjectTab";
import { RepositoryTab } from "@/routes/environments/create/tabs/RepositoryTab";
import IconArrowLeft from "central-icons/IconArrowLeft";
import IconBlankPagePortrait from "central-icons/IconBlankPagePortrait";
import IconChevronRightSmall from "central-icons/IconChevronRightSmall";
import IconLayoutGrid2 from "central-icons/IconLayoutGrid2";
import IconWorld from "central-icons/IconWorld";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";

const BackButton: FC<{ onClick: () => void; "data-tracking-id": string }> = ({
    onClick,
    "data-tracking-id": dataTrackingId,
}) => {
    return (
        <>
            <Button
                variant={"ghost"}
                size={"sm"}
                onClick={onClick}
                LeadingIcon={withGitpodSize(IconArrowLeft)}
                aria-label="Back"
                data-testid="context-select-back-button"
                className="absolute left-4 top-4 p-2 text-content-muted"
                data-tracking-id={dataTrackingId}
            />
            <span className="mb-6" />
        </>
    );
};

type Props = {
    initialContextURL: string;
    disableProjectSelection?: boolean;
    autoStart?: boolean;
    onClose: () => void;
};
export const CreateEnvironmentDetailsLayout: FC<Props> = ({
    onClose,
    disableProjectSelection = false,
    initialContextURL,
    autoStart = false,
}) => {
    useDocumentTitle("New environment");

    // is this member allowed to create environments from non-projects?
    const { requireProjects } = useRequireProject();

    const { contextURL, contextURLValid, setContextURL } = useContextURL(initialContextURL); // this serves the RepositoryTab as the controlled input value. We store it here because changing tabs re-renders the RepositoryTab, and we don't want to lose the input value when changing tabs or when authentication is required

    const [autoStartActive, setAutoStartActive] = useState<boolean>();

    const initialTab = useMemo(() => {
        if (contextURLValid) {
            setAutoStartActive(autoStart);
            return TabsValues.url;
        }
        if (disableProjectSelection) {
            return TabsValues.url;
        }

        return TabsValues.home;
    }, [autoStart, contextURLValid, disableProjectSelection]);
    const [activeTab, setActiveTab] = useAtom<TabValue | undefined>(activeTabAtom);

    // we have a special representation of NO_PROJECT_SELECTED so that we can distinguish between the explicit user choice of no project (which we don't want to override) and the default `undefined` value, which is safe to write over
    const [selectedProject, setSelectedProject] = useState<PlainProject | typeof NO_PROJECT_SELECTED | undefined>(
        undefined,
    );
    const [selectedClassEntry, setSelectedClassEntry] = useState<EnvironmentClassEntry>();
    const [stage, setStage] = useState<CreateEnvironmentStage>({ type: "input" });

    const { environmentClassEntries, isLoading: isLoadingEnvClasses } = useEnvironmentClassEntries();
    const hasNoEnvClasses = !isLoadingEnvClasses && environmentClassEntries.length === 0;

    const homeScreenCommandRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (activeTab === TabsValues.home) {
            requestAnimationFrame(() => {
                homeScreenCommandRef.current?.focus();
            });
        }
    }, [activeTab, homeScreenCommandRef]);

    if (hasNoEnvClasses) {
        return <NoEnvModal onClose={onClose} />;
    }

    if (stage.type === "scm-authentication") {
        return (
            <div className="flex size-full flex-col items-center justify-center gap-4 transition-height duration-500">
                <Authentication
                    repoURL={stage.contextUrl}
                    authenticationUrl={stage.authenticationUrl}
                    patAuth={stage.patAuth}
                    scmId={stage.scmId}
                    scmName={stage.scmName}
                    onClose={onClose}
                    onClickContinue={() => {
                        setStage({ type: "input" });
                        if (stage.onClickContinue) {
                            stage.onClickContinue();
                        }
                    }}
                    runnerId={stage.runner.runnerId}
                    data-track-location={TrackLocations.CreateEnvironmentSCMAuthenticationModal}
                />
            </div>
        );
    }

    const effectiveTab = activeTab ?? initialTab;
    switch (effectiveTab) {
        case "project":
            return (
                <>
                    <BackButton
                        onClick={() => setActiveTab(TabsValues.home)}
                        data-tracking-id="back-from-project-tab-create-environment-modal"
                    />
                    <DialogTitle className="mb-1.5 flex flex-row items-center gap-2">Start from project</DialogTitle>
                    <DialogDescription>Select or search for a project</DialogDescription>
                    <ProjectTab onClose={onClose} />
                </>
            );
        case "url":
            return (
                <>
                    <BackButton
                        onClick={() => {
                            setActiveTab(TabsValues.home);
                            setAutoStartActive(false); // after coming back from the url tab, we don't want to auto start the environment anymore
                        }}
                        data-tracking-id="back-from-url-tab-create-environment-modal"
                    />
                    <DialogTitle className="mb-1.5 flex flex-row items-center gap-2">Start from URL</DialogTitle>
                    <DialogDescription>
                        Type in the URL you want to start from. This can point to a git repository, pull request, issue,
                        or a branch.
                    </DialogDescription>
                    <RepositoryTab
                        onClose={onClose}
                        autoStart={autoStartActive}
                        contextURL={contextURL}
                        contextURLValid={contextURLValid}
                        selectedProject={selectedProject}
                        setSelectedProject={setSelectedProject}
                        setSelectedClassEntry={setSelectedClassEntry}
                        setAutoStartActive={setAutoStartActive}
                        setContextURL={setContextURL}
                        selectedClassEntry={selectedClassEntry}
                        setStage={setStage}
                    />
                </>
            );
        case "scratch":
            return (
                <>
                    <BackButton
                        onClick={() => setActiveTab(TabsValues.home)}
                        data-tracking-id="back-from-scratch-tab-create-environment-modal"
                    />
                    <DialogTitle className="mb-1.5 flex flex-row items-center gap-2">Start from scratch</DialogTitle>
                    <DialogDescription>Select your environment class</DialogDescription>
                    <BlankTab
                        onClose={onClose}
                        selectedClassEntry={selectedClassEntry}
                        setSelectedClassEntry={setSelectedClassEntry}
                    />
                </>
            );
    }

    const showFromProjectTab = !disableProjectSelection;
    const showFromScratchTab = !requireProjects && !disableProjectSelection;
    return (
        <>
            <DialogTitle className="mb-1.5">New environment</DialogTitle>
            <DialogDescription>Select how you want to start</DialogDescription>
            <Command
                tabIndex={0}
                className="mt-6 border-none shadow-none focus-visible:ring-0"
                ref={homeScreenCommandRef}
                autoFocus
                data-testid="new-environment-home"
            >
                <CommandList tabIndex={0} className="border-none *:flex *:flex-col *:gap-2 focus-visible:ring-0">
                    {showFromProjectTab && (
                        <ContextSelectionCard
                            title="Start from a project"
                            id="project"
                            Icon={
                                <div className="flex items-center justify-center rounded-[4.5px] bg-surface-brand-accent-01 p-1">
                                    <IconLayoutGrid2 size={16} className="text-content-brand-accent-02" />
                                </div>
                            }
                            onSelect={() => setActiveTab(TabsValues.project)}
                            data-testid="from-project-tab"
                        />
                    )}
                    <ContextSelectionCard
                        title="Start from a URL"
                        id="url"
                        Icon={
                            <div className="flex items-center justify-center rounded-[4.5px] bg-surface-brand-accent-04 p-1">
                                <IconWorld size={16} className="text-content-brand-accent-03" />
                            </div>
                        }
                        onSelect={() => setActiveTab(TabsValues.url)}
                        data-testid="from-url-tab"
                    />
                    {showFromScratchTab && (
                        <ContextSelectionCard
                            title="Start from scratch"
                            id="scratch"
                            Icon={
                                <div className="flex items-center justify-center rounded-[4.5px] bg-surface-brand-accent-08 p-1">
                                    <IconBlankPagePortrait size={16} className="text-content-brand-accent-06" />
                                </div>
                            }
                            onSelect={() => setActiveTab(TabsValues.scratch)}
                            data-testid="from-scratch-tab"
                        />
                    )}
                </CommandList>
            </Command>
        </>
    );
};

type ContextSelectionCardProps = {
    title: string;
    id: string;
    Icon: ReactNode;
    onSelect: () => void;
} & PropsWithTestId;
const ContextSelectionCard: FC<ContextSelectionCardProps> = ({
    title,
    id,
    Icon,
    onSelect,
    "data-testid": dataTestId,
}) => {
    return (
        <CommandItem
            value={id}
            className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-border-base p-4"
            onSelect={onSelect}
            data-testid={dataTestId}
        >
            <div className="flex flex-row items-center gap-3">
                {Icon}
                <span className="text-base font-normal text-content-primary">{title}</span>
            </div>
            <IconChevronRightSmall size={24} className="text-content-primary" />
        </CommandItem>
    );
};
